import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  LinearProgress,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountBalance as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/numberUtils';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface WalletTransaction {
  id: string;
  customerName: string;
  deviceType: string;
  orderNumber: number;
  totalCost: number;
  paidAmount: number;
  remainingAmount: number;
  walletNumber?: string;
  paymentMethod: string;
  deliveredAt: Date;
  notes?: string;
}

interface WalletReportData {
  vodafone: {
    amount: number;
    count: number;
    transactions: WalletTransaction[];
  };
  instapay: {
    amount: number;
    count: number;
    transactions: WalletTransaction[];
  };
  etisalat: {
    amount: number;
    count: number;
    transactions: WalletTransaction[];
  };
  totalAmount: number;
  totalCount: number;
}

interface InstapayVodafoneWalletReportProps {
  date: Date;
  branchId?: string;
}

const InstapayVodafoneWalletReport: React.FC<InstapayVodafoneWalletReportProps> = ({
  date,
  branchId
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WalletReportData>({
    vodafone: { amount: 0, count: 0, transactions: [] },
    instapay: { amount: 0, count: 0, transactions: [] },
    etisalat: { amount: 0, count: 0, transactions: [] },
    totalAmount: 0,
    totalCount: 0
  });

  useEffect(() => {
    loadWalletData();
  }, [date, branchId]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      setError(null);



      // إعداد نطاق التاريخ
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      // استعلام للحصول على جميع طلبات الصيانة المُسلمة
      const maintenanceQuery = query(
        collection(db, 'maintenance'),
        where('status', '==', 'delivered')
      );

      const snapshot = await getDocs(maintenanceQuery);


      const newData: WalletReportData = {
        vodafone: { amount: 0, count: 0, transactions: [] },
        instapay: { amount: 0, count: 0, transactions: [] },
        etisalat: { amount: 0, count: 0, transactions: [] },
        totalAmount: 0,
        totalCount: 0
      };

      snapshot.forEach(doc => {
        const record = doc.data();
        
        // فلترة التاريخ
        if (record.deliveredAt) {
          const deliveredDate = record.deliveredAt && typeof record.deliveredAt === 'object' && 'toDate' in record.deliveredAt
            ? (record.deliveredAt as any).toDate()
            : new Date(record.deliveredAt);

          if (deliveredDate < startOfDay || deliveredDate > endOfDay) {
            return; // تخطي إذا كان خارج النطاق الزمني
          }
        } else {
          return; // تخطي إذا لم يكن له تاريخ تسليم
        }

        // فلترة الفرع إذا تم تحديده
        if (branchId && record.branchId !== branchId) {
          return;
        }

        const paymentMethod = record.paymentMethod;
        const paidAmount = record.totalPaid || record.paid || 0;
        const totalCost = record.finalCost || record.estimatedCost || record.initialCost || 0;



        // التحقق من طريقة الدفع
        if (paymentMethod === 'vodafone_wallet' || paymentMethod === 'instapay' || paymentMethod === 'etisalat_wallet') {


          const transaction: WalletTransaction = {
            id: doc.id,
            customerName: record.customerName || 'غير محدد',
            deviceType: record.deviceType || 'غير محدد',
            orderNumber: record.orderNumber || 0,
            totalCost,
            paidAmount,
            remainingAmount: Math.max(0, totalCost - paidAmount),
            walletNumber: extractWalletNumber(record.notes),
            paymentMethod,
            deliveredAt: record.deliveredAt && typeof record.deliveredAt === 'object' && 'toDate' in record.deliveredAt
              ? (record.deliveredAt as any).toDate()
              : new Date(record.deliveredAt || Date.now()),
            notes: record.notes
          };

          // إضافة المعاملة للمحفظة المناسبة
          if (paymentMethod === 'vodafone_wallet') {
            newData.vodafone.amount += paidAmount;
            newData.vodafone.count += 1;
            newData.vodafone.transactions.push(transaction);
          } else if (paymentMethod === 'instapay') {
            newData.instapay.amount += paidAmount;
            newData.instapay.count += 1;
            newData.instapay.transactions.push(transaction);
          } else if (paymentMethod === 'etisalat_wallet') {
            newData.etisalat.amount += paidAmount;
            newData.etisalat.count += 1;
            newData.etisalat.transactions.push(transaction);
          }

          newData.totalAmount += paidAmount;
          newData.totalCount += 1;
        }
      });



      setData(newData);
    } catch (err) {
      console.error('❌ خطأ في تحميل بيانات المحافظ:', err);
      setError('فشل في تحميل بيانات المحافظ الإلكترونية');
    } finally {
      setLoading(false);
    }
  };

  // استخراج رقم المحفظة من الملاحظات
  const extractWalletNumber = (notes?: string): string => {
    if (!notes) return '';
    
    // البحث عن رقم بين أقواس
    const match = notes.match(/\(([^)]+)\)/);
    return match ? match[1] : '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          جاري تحميل بيانات المحافظ الإلكترونية...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadWalletData} sx={{ ml: 2 }}>
          إعادة المحاولة
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* زر التحديث */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadWalletData}
          disabled={loading}
        >
          تحديث البيانات
        </Button>
      </Box>

      {/* ملخص سريع */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(data.totalAmount)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    💳 إجمالي المحافظ
                  </Typography>
                  <Chip 
                    label={`${data.totalCount} معاملة`}
                    size="small"
                    sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
                <WalletIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#E60012', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(data.vodafone.amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    📱 فودافون كاش
                  </Typography>
                  <Chip 
                    label={`${data.vodafone.count} معاملة`}
                    size="small"
                    sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
                <PhoneIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#1976D2', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(data.instapay.amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    💳 انستاباي
                  </Typography>
                  <Chip 
                    label={`${data.instapay.count} معاملة`}
                    size="small"
                    sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#FF9800', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(data.etisalat.amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    📲 اتصالات كاش
                  </Typography>
                  <Chip 
                    label={`${data.etisalat.count} معاملة`}
                    size="small"
                    sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
                <PhoneIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* رسالة إذا لم توجد بيانات */}
      {data.totalCount === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1">
            📅 لا توجد معاملات محافظ إلكترونية في تاريخ {date.toLocaleDateString('ar-EG')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            تأكد من وجود طلبات صيانة تم تسليمها بالمحافظ الإلكترونية في هذا التاريخ
          </Typography>
        </Alert>
      )}

      {/* تفاصيل كل محفظة */}
      {data.vodafone.count > 0 && (
        <WalletSection 
          title="📱 فودافون كاش" 
          color="#E60012"
          walletData={data.vodafone}
        />
      )}

      {data.instapay.count > 0 && (
        <WalletSection 
          title="💳 انستاباي" 
          color="#1976D2"
          walletData={data.instapay}
        />
      )}

      {data.etisalat.count > 0 && (
        <WalletSection 
          title="📲 اتصالات كاش" 
          color="#FF9800"
          walletData={data.etisalat}
        />
      )}
    </Box>
  );
};

// مكون فرعي لعرض تفاصيل كل محفظة
interface WalletSectionProps {
  title: string;
  color: string;
  walletData: {
    amount: number;
    count: number;
    transactions: WalletTransaction[];
  };
}

const WalletSection: React.FC<WalletSectionProps> = ({ title, color, walletData }) => {
  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <Typography variant="h6">
            {title}
          </Typography>
          <Chip 
            label={`${walletData.count} معاملة`}
            size="small"
            sx={{ bgcolor: color, color: 'white' }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color }}>
            {formatCurrency(walletData.amount)}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>رقم الطلب</TableCell>
                <TableCell>العميل</TableCell>
                <TableCell>نوع الجهاز</TableCell>
                <TableCell>التكلفة الإجمالية</TableCell>
                <TableCell>المبلغ المدفوع</TableCell>
                <TableCell>المبلغ المتبقي</TableCell>
                <TableCell>رقم المحفظة</TableCell>
                <TableCell>وقت التسليم</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {walletData.transactions.map((transaction, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" sx={{ color }}>
                      #{transaction.orderNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{transaction.customerName}</TableCell>
                  <TableCell>{transaction.deviceType}</TableCell>
                  <TableCell>{formatCurrency(transaction.totalCost)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(transaction.paidAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {transaction.remainingAmount > 0 ? (
                      <Typography variant="body2" color="warning.main">
                        {formatCurrency(transaction.remainingAmount)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="success.main">
                        مدفوع بالكامل
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {transaction.walletNumber || 'غير محدد'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {transaction.deliveredAt.toLocaleString('ar-EG')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

export default InstapayVodafoneWalletReport;
